import { useLingui } from '@lingui/react/macro'
import { Button, ButtonLink } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { Avatar } from '@/routes/_app/-components/shared/avatar'
import { DemoChevron } from './demo-chevron'
import { InkBackdrop, INK } from './ink-backdrop'
import { Reveal } from './reveal'

// One vacancy's shortlist on a partner's platform, best fit first. The fit is what linking a professional to a position
// returns (POST /api/v1/jobs/:id/applicants/:user_id, `matchPercentage`).
const APPLICANTS = [
  { name: 'Laura Martín', fit: 91 },
  { name: 'Daniel Ortega', fit: 84 },
  { name: 'Sofía Ribas', fit: 76 },
  { name: 'Marc Vidal', fit: 62 },
] as const

/** The platforms' door, after the trial: the SDK and the API sold by what they put inside another product. */
export function ApiSection(props: { onDemo: () => void }) {
  const { t } = useLingui()
  return (
    <section id="api" className="scroll-mt-20 pb-20">
      <Reveal className="mx-auto max-w-6xl px-5 sm:px-6">
        <div
          className={cn(
            'relative isolate grid items-center gap-8 overflow-hidden rounded-3xl p-8 text-[#e8f3f0] md:grid-cols-[0.9fr_1.1fr] md:gap-12 md:p-12',
            INK,
          )}
        >
          <InkBackdrop />
          <div>
            <span className="text-xs font-semibold tracking-widest text-[#33ccaa] uppercase">{t`For platforms`}</span>
            <h2 className="mt-3.5 text-3xl font-semibold tracking-tight text-balance text-white">
              {t`Integrate psychographic fit into your own platform`}
            </h2>
            <p className="mt-4 leading-relaxed">
              {t`Let your users take the test and read their reports without leaving your environment, thanks to our SDK. Reach every profile in your system through the API to rank applicants by their fit level in each of your offers.`}
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              <Button size="lg" type="button" onClick={props.onDemo}>
                {t`Request a demo`}
                <DemoChevron />
              </Button>
              <ButtonLink
                size="lg"
                variant="outline"
                to="/docs"
                className="border-[#2b413c] bg-transparent text-[#e8f3f0] hover:bg-white/10 hover:text-white dark:bg-transparent"
              >
                {t`Documentation`}
              </ButtonLink>
            </div>
          </div>
          <Reveal delay={180} className="min-w-0">
            <PlatformWindow />
          </Reveal>
        </div>
      </Reveal>
    </section>
  )
}

/**
 * The partner's product, light on the dark panel so it reads as someone else's window: a vacancy's applicants ranked by
 * the fit D'Anchiano computes, and at its foot the one API call that brings that fit in. Fixed colours: the panel is the
 * same in both themes.
 */
function PlatformWindow() {
  const { t } = useLingui()
  return (
    <div className="min-w-0 overflow-hidden rounded-2xl bg-white text-[#1a2421] shadow-[0_24px_60px_-30px_rgb(0_0_0/0.6)]">
      <div className="flex items-center gap-1.5 border-b border-[#e6ecea] bg-[#f5f8f7] px-3.5 py-2.5">
        <span className="size-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="size-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="size-2.5 rounded-full bg-[#28c840]/80" />
        <span className="ml-3 truncate rounded-md bg-white px-2.5 py-0.5 text-[11px] text-[#6b7f7a]">
          {t`your-platform.com`}/jobs/1284
        </span>
      </div>
      <div className="px-5 pt-4 pb-3 sm:px-6">
        <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[15px] font-semibold tracking-tight">{t`Account Manager`}</span>
          <span className="text-xs text-[#6b7f7a]">{t`24 applicants`}</span>
          <span className="ml-auto rounded-full bg-[#33ccaa]/15 px-2.5 text-[10.5px] leading-5 font-semibold text-[#138a70]">
            {t`Fit by D'Anchiano`}
          </span>
        </div>
        <div className="mt-4 flex justify-between text-[10px] font-semibold tracking-wider text-[#8a9b97] uppercase">
          <span>{t`Applicant`}</span>
          <span>{t`Fit`}</span>
        </div>
        <ol className="mt-1 divide-y divide-[#eef2f1]">
          {APPLICANTS.map((applicant) => (
            <li key={applicant.name} className="flex items-center gap-3 py-2.5">
              <Avatar
                type="member"
                name={applicant.name}
                size="sm"
                forceInitials
                className="size-7 shrink-0 rounded-full text-[10px]"
              />
              <span className="min-w-0 flex-1 truncate text-[13px] font-medium">{applicant.name}</span>
              <span className="hidden h-1.5 w-24 overflow-hidden rounded-full bg-[#eef2f1] sm:block">
                <span className="block h-full rounded-full bg-[#33ccaa]" style={{ width: `${applicant.fit}%` }} />
              </span>
              <span className="w-9 text-right text-[13px] font-semibold tabular-nums">{applicant.fit}%</span>
            </li>
          ))}
        </ol>
      </div>
      <div className="border-t border-[#e6ecea] bg-[#f5f8f7] px-5 py-2.5 font-mono text-[10px] leading-relaxed [overflow-wrap:anywhere] text-[#6b7f7a] sm:px-6">
        <span className="text-[#138a70]">POST</span> /api/v1/jobs/1284/applicants/5531 →{' '}
        <span className="text-[#1a2421]">"matchPercentage": 91</span>
      </div>
    </div>
  )
}
